// MARK://dhk8/admin-shell/lib/clients/tenants
// Superadmin "Cuentas de terceros": lista de tenants, estado/plan, suspender/reactivar
// → dhk8-platform-tenants.
//   F3.1 / ADR-0009 — LECTURAS por el grafo:
//     query platformTenants        → todos los tenants (cross-tenant, superadmin)
//     query tenant(id)             → un tenant (badge SANDBOX/LIVE del shell)
//   ESCRITURAS por el grafo (F6.2 R3d) — lifecycle de AUTORIDAD, gating server-side
//   platform-tenants:tenant:suspend@* / activate@* (idéntico al REST):
//     mutation suspendTenant(id, reason)  /  mutation activateTenant(id)
//
// CONTRACT MISMATCH: PlatformTenant es { id, tenantId, name, slug, plan, active,
// suspendedAt?, deletedAt?, createdAt, updatedAt } — no hay legalName, ownerEmail,
// ni SANDBOX/LIVE (en el modelo Account eso es un switch client-side). Adaptamos:
// legalName ← name, status ← active ? (suspendedAt ? SUSPENDED : ACTIVE) : SUSPENDED,
// mode ← 'LIVE', ownerEmail ← ''. `goLive` no tiene backend — flip local del badge.
import { PREVIEW_STUB, mock } from '../env.js'
import { gqlClient } from '../gql.js'
import { PlatformTenantsQuery, PlatformTenantQuery, SuspendTenantMutation, ActivateTenantMutation } from '../queries/platform.queries.js'
import * as M from '../mock-data.js'


// ── Forma del nodo PlatformTenant del grafo ─────────────────────────────────
interface GraphTenant {
  id: string; tenantId: string; name: string; slug: string; plan: string
  active: boolean; suspendedAt?: string | null; deletedAt?: string | null
  createdAt: string; updatedAt: string
}

function toUiTenant(t: GraphTenant): M.TenantAccount {
  const status: M.TenantAccount['status'] = !t.active ? 'SUSPENDED' : t.suspendedAt ? 'SUSPENDED' : 'ACTIVE'
  return {
    id: t.id, name: t.name, legalName: t.name, plan: t.plan, status,
    mode: 'LIVE', createdAt: (t.createdAt ?? '').slice(0, 10), ownerEmail: '',
  }
}

const db = { tenants: structuredClone(M.TENANTS) }

export async function listTenants(signal?: AbortSignal): Promise<M.TenantAccount[]> {
  if (PREVIEW_STUB) return mock(db.tenants)
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(PlatformTenantsQuery, {}, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.platformTenants ?? []).map(toUiTenant)
}

export async function suspendTenant(tenantId: string): Promise<M.TenantAccount> {
  if (PREVIEW_STUB) { const t = db.tenants.find((x) => x.id === tenantId); if (t) t.status = 'SUSPENDED'; return mock(t!) }
  // Escritura por el grafo (gateada: tenant:suspend@*); relee por el grafo.
  const res = await gqlClient.mutation(SuspendTenantMutation, { id: tenantId, reason: 'superadmin' }).toPromise()
  if (res.error) throw res.error
  return (await getTenant(tenantId)) ?? { id: tenantId, name: '—', legalName: '—', plan: '', status: 'SUSPENDED', mode: 'LIVE', createdAt: '', ownerEmail: '' }
}

export async function reactivateTenant(tenantId: string): Promise<M.TenantAccount> {
  if (PREVIEW_STUB) { const t = db.tenants.find((x) => x.id === tenantId); if (t) t.status = 'ACTIVE'; return mock(t!) }
  const res = await gqlClient.mutation(ActivateTenantMutation, { id: tenantId }).toPromise()
  if (res.error) throw res.error
  return (await getTenant(tenantId)) ?? { id: tenantId, name: '—', legalName: '—', plan: '', status: 'ACTIVE', mode: 'LIVE', createdAt: '', ownerEmail: '' }
}

/** Current tenant (so the admin shell can show the SANDBOX/LIVE badge). */
export async function getTenant(tenantId: string, signal?: AbortSignal): Promise<M.TenantAccount | null> {
  if (PREVIEW_STUB) {
    const t = db.tenants.find((x) => x.id === tenantId)
    return mock(t ?? { id: tenantId, name: 'Mi negocio', legalName: '—', plan: 'restaurant-pro', status: 'ACTIVE', mode: 'SANDBOX', createdAt: new Date().toISOString().slice(0, 10), ownerEmail: '' })
  }
  try {
    const ctx = signal ? { fetchOptions: { signal } } : undefined
    const res = await gqlClient.query(PlatformTenantQuery, { id: tenantId }, ctx).toPromise()
    if (res.error) throw res.error
    const t = res.data?.tenant
    return t ? toUiTenant(t) : null
  } catch { return null }
}

/** "Go live": en el modelo Account (estilo Stripe) el tenant live siempre existe,
 *  así que esto es un switch de env puramente client-side — no hay mutación backend. */
export async function goLive(tenantId: string): Promise<M.TenantAccount> {
  if (PREVIEW_STUB) { const t = db.tenants.find((x) => x.id === tenantId); if (t) t.mode = 'LIVE'; return mock(t!) }
  const t = await getTenant(tenantId)
  return { ...(t ?? { id: tenantId, name: 'Mi negocio', legalName: '—', plan: '', status: 'ACTIVE' as const, createdAt: '', ownerEmail: '' }), mode: 'LIVE' }
}
